/**
 * Derive the trackable lead sources on a landing page from its actual structure.
 *
 * The brief's principle: LinkHub owns the interaction; the tracking layer owns
 * the destination. So lead sources are discovered from the page config rather
 * than configured by hand, and each gets a STABLE, non-PII id derived from the
 * section id + role — never from the phone number/email value (which may be
 * data-bound and change per visitor).
 *
 * Current sources (matches how the public page renders tel:/mailto:):
 *   - the contact form            -> form_submission        id: "form"
 *   - agent_card phone / email    -> click_to_call/email    id: "<sectionId>:phone|email"
 *   - dynamic_agent phone / email -> click_to_call/email    id: "<sectionId>:phone|email"
 *
 * Both the Tracking tab (editor) and the public page compute ids through here,
 * so a configured source and a fired source always agree.
 */

export const LEAD_TYPE_LABEL = {
  form_submission: 'Form',
  click_to_call: 'Click to Call',
  email_click: 'Click to Email',
};

/** Non-PII, GA4-safe slug for the lead_identifier param. Never fed a raw value. */
export function slugifyIdentifier(s, fallback = 'lead') {
  const slug = String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
  return slug || fallback;
}

/** Stable id for a section-scoped lead source. */
export function leadSourceId(sectionId, role) {
  return `${sectionId}:${role}`;
}

export function deriveLeadSources(page, brand) {
  if (!page) return [];
  const sections = Array.isArray(page.sections) ? page.sections : [];
  const sources = [];

  // Spec §5: the identifier must stay STABLE if the visible label changes.
  // The derived value seeds a source the first time it's configured; from then
  // on the persisted identifier wins, so renaming an agent or a form never
  // silently re-points GA4 reporting at a new lead_identifier.
  const stored = (id) => page.lead_tracking?.[id]?.identifier;
  const push = (src) => sources.push({ ...src, identifier: stored(src.id) || src.identifier });

  // One form per page — every contact_form section posts to the same endpoint,
  // so a single form_submission source is correct.
  const formSection = sections.find((s) => s.type === 'contact_form' && s.visible !== false);
  if (page.form || formSection) {
    const label = page.form?.name || formSection?.content?.title || 'Contact form';
    push({
      id: 'form',
      type: 'form_submission',
      label,
      sublabel: 'Form submission',
      // GA4 brief §5: use the stable page/campaign identifier, NOT the visible
      // form heading/copy — e.g. "dubai-africa-roadshow" -> dubai_africa_roadshow.
      identifier: slugifyIdentifier(page.slug || page.title, 'form'),
    });
  }

  for (const s of sections) {
    if (!s || s.visible === false) continue;
    const c = s.content || {};

    if (s.type === 'agent_card') {
      const name = c.agent_name || 'Agent';
      // GA4 brief §5/§9: the identifier names the CONTACT, and is the same for
      // their call and email CTAs — lead_type already says which happened.
      const contact = slugifyIdentifier(name, 'agent');
      if (c.agent_phone) {
        push({
          id: leadSourceId(s.id, 'phone'), type: 'click_to_call',
          label: name, sublabel: c.agent_phone, identifier: contact,
        });
      }
      if (c.agent_email) {
        push({
          id: leadSourceId(s.id, 'email'), type: 'email_click',
          label: name, sublabel: 'Email', identifier: contact,
        });
      }
    } else if (s.type === 'dynamic_agent') {
      // The agent's phone/email resolve at runtime from reference data, so we
      // can't know here whether both exist — expose both as potential sources;
      // the dispatcher only fires for a link the visitor actually clicks.
      // The agent resolves at runtime, so the identifier here is only a
      // placeholder — the public page overrides it with the resolved agent's
      // own slug at fire time, so each agent is distinguished correctly (§7).
      const name = c.title || 'Agent';
      const contact = slugifyIdentifier(name, 'agent');
      push({
        id: leadSourceId(s.id, 'phone'), type: 'click_to_call',
        label: name, sublabel: 'Agent phone', identifier: contact,
      });
      push({
        id: leadSourceId(s.id, 'email'), type: 'email_click',
        label: name, sublabel: 'Agent email', identifier: contact,
      });
    }
  }

  // The brand footer also renders tel:/mailto: links on every public page, so
  // they're lead-generating actions too (spec §3: discover ALL of them). Ids are
  // fixed strings — the footer isn't a section — and the identifier is generic
  // rather than the number/address itself (§5 privacy).
  const footer = brand?.footer;
  if (footer?.phone) {
    push({
      id: 'footer:phone', type: 'click_to_call',
      label: 'Footer', sublabel: footer.phone, identifier: 'footer_call',
    });
  }
  if (footer?.email) {
    push({
      id: 'footer:email', type: 'email_click',
      label: 'Footer', sublabel: 'Email', identifier: 'footer_email',
    });
  }

  return sources;
}
